// @ts-nocheck

"use client";

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { AnimatePresence, motion } from "motion/react";

import useWindowSize from "@/lib/hooks/use-window-size";

import Leaflet from "./leaflet";

export default function Modal({
  children,
  showModal,
  setShowModal,
  preventBackgroundClose,
}: {
  children: React.ReactNode;
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  preventBackgroundClose?: boolean;
}) {
  const desktopModalRef = useRef(null);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowModal(false);
      }
    },
    [setShowModal],
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  // Guard against a stale `document.body.style.pointerEvents = "none"` left
  // behind by a Radix DismissableLayer (e.g. the vaul Drawer or a
  // DropdownMenu) that is still finishing its close/unmount when this modal
  // opens. Radix tracks that lock via a single module-level variable shared
  // across every Dialog/Drawer/DropdownMenu/etc. in the app, restored only
  // once the closing layer actually unmounts (which can happen after this
  // modal has already rendered). Since this modal is a plain fixed div and
  // never opts into Radix's own layering, it must actively keep the body
  // clickable for as long as it's shown, or every click on it (and the rest
  // of the page) silently no-ops until a hard refresh clears the stray
  // inline style.
  useEffect(() => {
    if (!showModal) return;

    document.body.style.pointerEvents = "";

    const observer = new MutationObserver(() => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => observer.disconnect();
  }, [showModal]);

  useEffect(() => {
    if (!showModal || !desktopModalRef.current) return;
    const el = desktopModalRef.current;
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (
        e.shiftKey
          ? document.activeElement === first
          : document.activeElement === last
      ) {
        e.preventDefault();
        (e.shiftKey ? last : first)?.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [showModal]);

  const { isMobile, isDesktop } = useWindowSize();

  return (
    <AnimatePresence>
      {showModal && (
        <>
          {isMobile && <Leaflet setShow={setShowModal}>{children}</Leaflet>}
          {isDesktop && (
            <>
              <div
                ref={desktopModalRef}
                className="fixed inset-0 z-[200] hidden min-h-screen items-center justify-center md:flex"
                onMouseDown={(e) => {
                  if (
                    desktopModalRef.current === e.target &&
                    !preventBackgroundClose
                  ) {
                    setShowModal(false);
                  }
                }}
              >
                <motion.div
                  key="desktop-modal"
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.95 }}
                >
                  {children}
                </motion.div>
              </div>
              <motion.div
                key="desktop-backdrop"
                className="fixed inset-0 z-[199] bg-black/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  if (!!preventBackgroundClose) return;

                  setShowModal(false);
                }}
              />
            </>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
